import { sleep } from '@last/core'

import { versionApi } from '@/api/client'
import { logger } from '@/monitoring'
import type { Device } from '@/types/device'

export type State = 'idle' | 'checking' | 'downloading' | 'ready' | 'updating'
export type DownloadInfo = {
  version: string
  releaseDate?: string
  url: string
  size?: number
  checksum: string
  deprecated?: boolean
}

export abstract class Updater {
  private state: State
  private nextVersion: string

  constructor() {
    this.state = 'idle'
    this.nextVersion = ''
  }

  async checkUpdates(device: Device): Promise<boolean> {
    if (['checking', 'downloading'].includes(this.state)) {
      return false
    }

    this.state = 'checking'

    try {
      const platform = await this._getPlatform()
      const response = await versionApi.getUpdates({
        version: device.appVersion,
        model: device.model,
        platform
      })

      if (!response.version) {
        this.state = 'idle'
        return false
      }

      if (response.version === this.nextVersion) {
        this.state = 'ready'
        this.nextVersion = response.version
        return true
      }

      this.state = 'downloading'
      await this.retryWithDelay(() => this._downloadUpdate(response))
      this.state = 'ready'
      this.nextVersion = response.version
      return true
    } catch (error) {
      this.state = 'idle'
      logger.warn('Error updating device', {
        error
      })
      return false
    }
  }

  /**
   * To call when the app is started to notify that everything is working ok
   * Not abstract because it is only really needed in Capgo
   */
  async notifyAppReady() {}

  /**
   * Retries a promise-returning function with exponential backoff
   */
  private async retryWithDelay<T>(
    fn: () => Promise<T>,
    maxRetries = 20,
    delayMs = 30_000
  ): Promise<T> {
    let lastError: Error

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn()
      } catch (error) {
        lastError = error as Error
        await sleep(delayMs)
      }
    }

    throw lastError!
  }

  abstract getVersion(): Promise<{
    name: string
    build: string
    native?: string
  }>

  abstract _getPlatform(): Promise<'windows' | 'win32' | 'bundle' | 'android' | 'ios' | 'apk'>

  abstract _downloadUpdate(_info: DownloadInfo): Promise<void>

  abstract installUpdate(): Promise<void>
}
